import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Book, Clock, GraduationCap, TrendingUp } from "lucide-react";
import axios from "axios";

const CoursesList = () => {
  const [courses, setCourses] = useState([]);
  const [courseNames, setCourseNames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [finalCourses, setFinalCourses] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setIsLoading(true);
        axios.defaults.withCredentials = true;

        // Log the full response to understand its structure
        const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/course/get-all-course-ids`);
        const courseIds = response.data?.courses;
        setCourseNames(courseIds);

        // Fetch details for each course
        const courseDetailsPromises = courseIds.map(async (courseId) => {
          try {
            const detailResponse = await axios.post(
              `${process.env.REACT_APP_BASE_URL}/course/get-all-course-data-student`,
              { courseID: courseId }
            );
            console.log(`Course details for ${courseId}:`, detailResponse.data);
            return {courseId, details: detailResponse.data.data};
          } catch (courseDetailError) {
            console.error(`Error fetching details for course ${courseId}:`, courseDetailError);
            return null;  
          }
        });

        const courseDetails = await Promise.all(courseDetailsPromises);

        const combinedCourses = courseDetails.map(({ courseId, details }) => ({
          courseId, // Name of the course
          ...details, // All additional details fetched from the API
        }));
        setCourses(courseDetails);
        setFinalCourses(combinedCourses);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching courses:", error);
        setError(`Failed to load courses: ${error.message}`);
        setIsLoading(false);
      }
    };

    fetchCourses();
  }, []);

  const getProgressColor = (progress) => {
    if (progress >= 90) return 'bg-green-100 text-green-800';
    if (progress >= 75) return 'bg-blue-100 text-blue-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen text-xl text-red-600">
        {error}
      </div>
    );
  }

  // No courses state
  if (courses.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen text-xl text-gray-600">
        No courses found
      </div>
    );
  }
  console.log(finalCourses)
  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md border-t-4 border-blue-500 mb-6">
        <div className="p-6">
          <h1 className="text-2xl font-bold">Course Performance Dashboard</h1>
          <p className="text-gray-600 mt-2">Track your academic progress across all courses</p>
        </div>
      </div>

      {/* Course Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {finalCourses.map((course) => (
          <Link to={`${course.courseId}`} key={course.courseId}>
            <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="p-6">
                {/* Course Header */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-xl font-bold">{course.courseId || 'Unnamed Course'}</h2>
                    <p className="text-gray-600 text-sm">{course?.courseDescription || ''}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getProgressColor(course?.progress || 50)}`}>
                    {course?.progress || 50}%
                  </span>
                </div>

                {/* Course Details */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-gray-600">
                    <GraduationCap className="w-4 h-4" />
                    <span>{course.instructor || 'Prof. 1234'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>{course.schedule || 'Mon-Fri'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Book className="w-4 h-4" />
                    <span>{course.credits || 4} Credits</span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${course.progress || 0}%` }}
                    />
                  </div>
                </div>

                {/* Assessment Summary */}
                <div className="grid grid-cols-3 gap-4 mt-4 text-center text-sm">
                  <div className="bg-gray-50 p-2 rounded">
                    <div className="font-semibold">{course.quizzes?.length || 0}</div>
                    <div className="text-gray-600">Quizzes</div>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <div className="font-semibold">{course.assignments?.length || 0}</div>
                    <div className="text-gray-600">Assignments</div>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <div className="font-semibold">{course.exams?.length || 0}</div>
                    <div className="text-gray-600">Exams</div>
                  </div>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default CoursesList;